import React, { useState, useEffect, useContext, useRef } from 'react';
import io from 'socket.io-client';

import { UserContext } from '~contexts/user';

export const SocketContext = React.createContext();

const Socket = ({ domain, path, addTrailingSlash, children }) => {
  const [socket, setSocket] = useState(null);
  const [status, setStatus] = useState('not-initialized');

  useEffect(() => {
    if (status === 'not-initialized') {
      setSocket(
        io(domain, {
          transports: ['polling'],
          path,
          reconnection: false,
          autoConnect: false,
          addTrailingSlash,
        })
      );
      setStatus('initialized');
    }

    return () => {
      if (socket) {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('pong');
        socket.disconnect();
      }
    };
  }, [status, socket, domain, path, addTrailingSlash]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        status,
      }}
    >
      <SocketRoom>{children}</SocketRoom>
    </SocketContext.Provider>
  );
};

const SocketRoom = ({ children }) => {
  const { socket } = useContext(SocketContext);
  const { user } = useContext(UserContext);

  const { personId } = user;

  useEffect(() => {
    if (socket && personId && !socket.connected) {
      socket.connect();
      socket.emit('JOINED', { person_id: personId });
      // socket.onAny((event, ...args) => {
      //   console.log(`got ${event} with args:`, args);
      // });
      return;
    }

    // If no websocketChannelName and we're connected - disconnect
    if (socket && socket.status === 'connected') {
      socket.emit('terminate');
    }
  }, [personId, socket]);

  useEffect(() => {
    return () => {
      if (socket && socket.connected) {
        socket.emit('terminate');
      }
    };
  }, [socket]);

  return <>{children}</>;
};

export const useWebsocketEvent = (event, handler) => {
  const { socket } = useContext(SocketContext);
  const handlerRef = useRef(handler);

  if (socket === undefined) {
    throw new Error('useEvent must be used within a SocketProvider');
  }

  // Keep handler ref updated
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!socket) return;

    const eventHandler = (...args) => handlerRef.current(...args);
    socket.on(event, eventHandler);

    return () => {
      socket.off(event, eventHandler);
    };
  }, [socket, event]);
};

export default Socket;
